import { useEffect, useState } from "react";
import HttpService from "../../services/HttpService";

import ReportCard from "./reporting/ReportCard";

import "../../scss/Reports.scss";
import { applicationColumns, settlementColumns, productColumns, typeColumns, collateralColumns, brokerColumns, loanBookColumns } from "./reporting/ReportColumns";

export default function ReportingDashboard() {
    const defaultPeriods = [
        // "7 Days",
        // "30 Days",
        // "MTD",
        // "QTD",
        // "FYTD",
        "6m",
        "12m",
        "24m"
    ];

    const [reportData, setReportData] = useState({
        applications: [],
        settlements: [],
        products: [],
        types: [],
        collateral: [],
        brokers: [],
        loanBook: []
    });

    const [loading, setLoading] = useState({
        applications: true,
        settlements: true,
        products: true,
        types: true,
        collateral: true,
        brokers: true,
        loanBook: true
    });
    const [error, setError] = useState(null);

    const handlePeriodChange = async (reportKey, endpoint, period) => {
        setLoading((prev) => ({
            ...prev,
            [reportKey]: true
        }));

        try {
            const data = await HttpService.get(
                `${endpoint}/${period}`
            );

            setReportData((prev) => ({
                ...prev,
                [reportKey]: data
            }));
        } catch (err) {
            console.error(`Failed to load ${reportKey} report:`, err);
        } finally {
            setLoading((prev) => ({
                ...prev,
                [reportKey]: false
            }));
        }
    };

    useEffect(() => {
        const fetchReports = async () => {
            try {
                setLoading(true);

                const [
                    applications,
                    settlements,
                    products,
                    types,
                    collateral,
                    brokers,
                    loanBook,
                ] = await Promise.all([
                    HttpService.get("reporting/applications/month/6m"),
                    HttpService.get("reporting/settlements/6m"),
                    HttpService.get("reporting/applications/product/6m"),
                    HttpService.get("reporting/applications/type/6m"),
                    HttpService.get("reporting/collateral/6m"),
                    HttpService.get("reporting/applications/broker/6m"),
                    HttpService.get("reporting/loanbook")
                ]);

                setReportData({
                    applications,
                    settlements,
                    products,
                    types,
                    collateral,
                    brokers,
                    loanBook
                });

                setLoading({
                    applications: false,
                    settlements: false,
                    products: false,
                    types: false,
                    collateral: false,
                    brokers: false,
                    loanBook: false
                });
            } catch (err) {
                console.error("Failed to load reporting data:", err);
                setError("Failed to load reporting data.");

                setLoading({
                    applications: false,
                    settlements: false,
                    products: false,
                    types: false,
                    collateral: false,
                    brokers: false,
                    loanBook: false
                });
            } finally {
                setLoading(false);
            }
        };

        fetchReports();
    }, []);

    return (
        <div className="dashboard-container">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                <ReportCard
                    title="Loan Applications by Month"
                    subtitle="Volume and value of new loan applications received"
                    columns={applicationColumns}
                    data={reportData.applications}
                    loading={loading.applications}
                    periods={defaultPeriods}
                    onPeriodChange={(period) =>
                        handlePeriodChange(
                            "applications",
                            "reporting/applications/month",
                            period
                        )
                    }
                    displayFooter={true}
                />

                <ReportCard
                    title="Loan Settlements by Month"
                    subtitle="Volume and value of loans settled"
                    columns={settlementColumns}
                    data={reportData.settlements}
                    loading={loading.settlements}
                    periods={defaultPeriods}
                    onPeriodChange={(period) =>
                        handlePeriodChange(
                            "settlements",
                            "reporting/settlements",
                            period
                        )
                    }
                    displayFooter={true}
                />

                <ReportCard
                    title="Loan Applications by Product"
                    subtitle="Application mix across the product suite"
                    columns={productColumns}
                    data={reportData.products}
                    loading={loading.products}
                    periods={defaultPeriods}
                    onPeriodChange={(period) =>
                        handlePeriodChange(
                            "products",
                            "reporting/applications/product",
                            period
                        )
                    }
                    displayFooter={true}
                />

                <ReportCard
                    title="Loan Applications by Type"
                    subtitle="Residential, commercial and SMSF split"
                    columns={typeColumns}
                    data={reportData.types}
                    loading={loading.types}
                    periods={defaultPeriods}
                    onPeriodChange={(period) =>
                        handlePeriodChange(
                            "types",
                            "reporting/applications/type",
                            period
                        )
                    }
                    displayFooter={true}
                />

                <ReportCard
                    title="Loan Applications by Collateral State"
                    subtitle="Security property location"
                    columns={collateralColumns}
                    data={reportData.collateral}
                    loading={loading.collateral}
                    periods={defaultPeriods}
                    onPeriodChange={(period) =>
                        handlePeriodChange(
                            "collateral",
                            "reporting/collateral",
                            period
                        )
                    }
                    displayFooter={true}
                />

                <ReportCard
                    title="Loan Applications by Broker State"
                    subtitle="Originating broker location"
                    columns={brokerColumns}
                    data={reportData.brokers}
                    loading={loading.brokers}
                    periods={defaultPeriods}
                    onPeriodChange={(period) =>
                        handlePeriodChange(
                            "brokers",
                            "reporting/applications/broker",
                            period
                        )
                    }
                    displayFooter={true}
                />

                <ReportCard
                    title="Total Loan Book Size by Month"
                    subtitle="Cumulative settled loan book balance"
                    columns={loanBookColumns}
                    data={reportData.loanBook}
                    loading={loading.loanBook}
                    onPeriodChange={(period) =>
                        handlePeriodChange(
                            "loanBook",
                            "reporting/loanbook",
                            period
                        )
                    }
                    displayFooter={false}
                />

            </div>
        </div>
    );
}